/**
 * App - Root component with routing.
 * 
 * ARCHITECTURE: Socket.io connection hierarchy
 * 
 * Pages (src/pages/) manage Socket.io via useSocket():
 * - LobbyScreen: Connects for lobby events (player:joined, etc.)
 * - GamePage: Connects for game events (game:tv:region_updated, etc.)
 * 
 * Views (src/views/) NEVER call useSocket():
 * - TVView, PhoneView: Use hooks (useGameDisplay) that assume socket connected
 * 
 * Hooks (src/hooks/) register listeners on existing socket:
 * - useGameDisplay: Registers game:tv:region_updated, game:phone:info_updated
 * - usePlayerEvents: Registers player:joined, player:left
 * - DO NOT call getSocket() or connectSocket() in hooks
 */

import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Toaster } from 'sonner'
import { useTranslation } from 'react-i18next'
import { HomePage } from '@/pages/HomePage'
import { HostPage } from '@/pages/HostPage'
import { JoinPage } from '@/pages/JoinPage'
import { LobbyScreen } from '@/pages/LobbyScreen'
import { GamePage } from '@/pages/GamePage'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { AuthCallbackPage } from '@/pages/AuthCallbackPage'
import { BillingSuccessPage } from '@/pages/BillingSuccessPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { WalletHistoryPage } from '@/pages/WalletHistoryPage'
import { LegalPage } from '@/pages/LegalPage'
import { TermsGatePage } from '@/pages/TermsGatePage'
import { PairDevicePage } from '@/pages/PairDevicePage'
import { RequireAuth } from '@/components/RequireAuth'
import { CookieBanner } from '@/components/medium/CookieBanner'
import { useAuthStore } from '@/stores/authStore'
import termsContent from '@/content/legal/regulamin.md?raw'
import privacyContent from '@/content/legal/polityka-prywatnosci.md?raw'
import cookiesContent from '@/content/legal/polityka-cookies.md?raw'

function App() {
  const { t } = useTranslation()
  const initAuth = useAuthStore((s) => s.initAuth)

  useEffect(() => {
    initAuth().then(() => {
      // Post-auth locale sync (priority 2):
      // Custom detector ran synchronously before initAuth resolved (user was null).
      // Now that initAuth has completed, apply user's saved locale if available.
      const locale = useAuthStore.getState().user?.locale
      if (locale) {
        // Dynamic import avoids static dependency on i18next at module load time
        void import('i18next').then(({ default: i18next }) => {
          i18next.changeLanguage(locale)
        })
      }
    })
  }, [initAuth])

  return (
    <div className="min-h-screen">
      <Toaster
        theme="dark"
        richColors
        toastOptions={{
          // Tokens hold bare HSL channels, so they must be wrapped in hsl()
          // here — sonner sets these as raw inline CSS values.
          style: {
            background: 'hsl(var(--card))',
            border: '2px solid hsl(var(--pixel-outline))',
            borderRadius: '0',
            boxShadow: '4px 4px 0 0 hsl(var(--pixel-outline))',
            color: 'hsl(var(--foreground))',
          },
        }}
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/regulamin"
          element={<LegalPage title={t('legal.termsTitle')} content={termsContent} />}
        />
        <Route
          path="/polityka-prywatnosci"
          element={<LegalPage title={t('legal.privacyTitle')} content={privacyContent} />}
        />
        <Route
          path="/polityka-cookies"
          element={<LegalPage title={t('legal.cookiesTitle')} content={cookiesContent} />}
        />
        <Route
          path="/akceptacja-regulaminu"
          element={
            <RequireAuth>
              <TermsGatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/host/:gameMode"
          element={
            <RequireAuth>
              <HostPage />
            </RequireAuth>
          }
        />
        <Route
          path="/billing/success"
          element={
            <RequireAuth>
              <BillingSuccessPage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile/coins"
          element={
            <RequireAuth>
              <WalletHistoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/pair/:code"
          element={
            <RequireAuth>
              <PairDevicePage />
            </RequireAuth>
          }
        />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/join/:code" element={<JoinPage />} />
        <Route path="/session/:code/lobby" element={<LobbyScreen />} />
        
        {/* CRITICAL: All /game routes render GamePage which manages Socket.io */}
        <Route path="/game/:sessionId" element={<GamePage />} />
        <Route path="/game/:sessionId/tv" element={<GamePage />} />
        <Route path="/game/:sessionId/phone" element={<GamePage />} />
      </Routes>
      <CookieBanner />
    </div>
  )
}

export default App
